import type { EmulatorBackend, LoadProgress, SystemId } from './types';
import { createBackend } from './registry';
import { SYSTEMS } from './systems';
import type { InputManager } from '../input/InputManager';
import { getBiosRecord, getRomFileBlobs, abortRomFileBlobs, markPlayed, updateRom, type RomMeta } from '../storage/romRepository';
import { activeCheatCodes } from '../storage/cheatRepository';
import { decodeCueBuffer, isCueFilename, normalizeKey, parseCueFileRefs } from './disc/cue';
import { isM3uFilename, parseM3uEntries } from './disc/m3u';
import type { ShaderId } from './video/shaders';
import {
  AUTOSAVE_SLOT,
  loadSram,
  sramKeyFor,
  loadState as loadStateRecord,
  listStates,
  saveSram,
  deleteSram,
  getSaveRecord,
  saveState as saveStateRecord,
  DEFAULT_PSX_MEMCARD_ID,
  isFormattedPsxMemcard,
  inspectPsxMemcard,
  isPsxMemcardConsistent,
  normalizePsxMemcardForBoot,
  type PsxMemcardUsage,
} from '../storage/saveRepository';
import { requestSync } from '../storage/cloudSync';

export interface SessionEvents {
  onFps?: (fps: number) => void;
  onError?: (e: Error) => void;
  onStatus?: (status: SessionStatus) => void;
  /** 起動中の進捗（0–100%） */
  onProgress?: (p: LoadProgress) => void;
  /** 巻き戻しが使えないと判明したときの通知 */
  onRewindUnavailable?: (reason: string) => void;
}

export type SessionStatus = 'idle' | 'loading' | 'running' | 'paused' | 'error';

/** 読み込み中にユーザーがキャンセルした。エラー画面には出さない。 */
export class SessionCancelledError extends Error {
  constructor() {
    super('起動をキャンセルしました');
    this.name = 'SessionCancelledError';
  }
}

export interface SessionOptions {
  rom: RomMeta;
  container: HTMLElement;
  input: InputManager;
  volume: number;
  preferredBackendId?: string;
  /** 巻き戻しを有効にして起動する */
  rewind?: boolean;
  /** 縦方向を整数倍に丸める */
  integerScale?: boolean;
  /** 表示フィルタ */
  shader?: ShaderId;
  /** エミュレーションを Worker で走らせる（実験的） */
  useWorker?: boolean;
  /** PS1 で使うメモリーカード ID（設定の activePsxMemcardId） */
  psxMemcardId?: string;
  events?: SessionEvents;
}

/** SRAM を書き戻す間隔。頻繁すぎると IndexedDB がボトルネックになる。 */
const SRAM_FLUSH_INTERVAL = 10_000;
/** オートセーブ間隔 */
const AUTOSAVE_INTERVAL = 60_000;

/**
 * 「ROM を選んでから遊び終わるまで」を束ねるオブジェクト。
 *
 * バックエンド選択・ROM/BIOS/セーブの読み込み・定期セーブ・
 * ライフサイクル（バックグラウンド遷移での自動ポーズ）をここに閉じ込め、
 * React 側は start/stop と状態表示だけを見ればよいようにする。
 */
export class EmulatorSession {
  readonly rom: RomMeta;
  private backend: EmulatorBackend | null = null;
  private backendId = '';
  private input: InputManager;
  private events: SessionEvents;
  private container: HTMLElement;
  private volume: number;
  private preferredBackendId: string | undefined;
  private rewindRequested: boolean;
  private integerScale: boolean;
  private cheatsAvailable = false;
  private shader: ShaderId;
  private useWorker: boolean;
  private psxMemcardId: string;

  private status: SessionStatus = 'idle';
  private sramTimer: number | undefined;
  private autosaveTimer: number | undefined;
  private detachLifecycle: (() => void) | null = null;
  /** start() 進行中に dispose / キャンセルされたら true */
  private cancelled = false;
  private disposed = false;
  /** dispose 時に await を即打ち切るための待ち合わせ解除 */
  private cancelWaiters: Array<() => void> = [];
  /** ROM IDB 読み出しの Abort */
  private loadAbort: AbortController | null = null;

  constructor(opts: SessionOptions) {
    this.rom = opts.rom;
    this.container = opts.container;
    this.input = opts.input;
    this.volume = opts.volume;
    this.preferredBackendId = opts.preferredBackendId;
    this.rewindRequested = opts.rewind ?? false;
    this.integerScale = opts.integerScale ?? false;
    this.shader = opts.shader ?? 'none';
    this.useWorker = opts.useWorker ?? false;
    this.psxMemcardId = opts.psxMemcardId ?? DEFAULT_PSX_MEMCARD_ID;
    this.events = opts.events ?? {};
  }

  get currentStatus(): SessionStatus {
    return this.status;
  }

  get currentBackendId(): string {
    return this.backendId;
  }

  private setStatus(s: SessionStatus): void {
    this.status = s;
    this.events.onStatus?.(s);
  }

  private reportProgress(percent: number, label: string): void {
    const clamped = Math.max(0, Math.min(100, Math.round(percent)));
    this.events.onProgress?.({ percent: clamped, label });
  }

  private throwIfCancelled(): void {
    if (this.cancelled || this.disposed) throw new SessionCancelledError();
  }

  /** dispose されたら即 SessionCancelledError で落とす await。 */
  private awaitUnlessCancelled<T>(
    promise: Promise<T>,
    onAbandoned?: (value: T) => void,
  ): Promise<T> {
    this.throwIfCancelled();
    return new Promise<T>((resolve, reject) => {
      const onCancel = () => reject(new SessionCancelledError());
      this.cancelWaiters.push(onCancel);
      promise.then(
        (value) => {
          this.cancelWaiters = this.cancelWaiters.filter((w) => w !== onCancel);
          if (this.cancelled || this.disposed) {
            try {
              onAbandoned?.(value);
            } catch (e) {
              console.warn('[session] abandoned cleanup failed', e);
            }
            reject(new SessionCancelledError());
          } else {
            resolve(value);
          }
        },
        (err: unknown) => {
          this.cancelWaiters = this.cancelWaiters.filter((w) => w !== onCancel);
          reject(err);
        },
      );
    });
  }

  private wakeCancelWaiters(): void {
    const waiters = this.cancelWaiters;
    this.cancelWaiters = [];
    for (const wake of waiters) wake();
  }

  /** ROM/BIOS/セーブを読み込んでエミュレーションを開始する。 */
  async start(): Promise<void> {
    if (this.disposed) throw new SessionCancelledError();
    this.cancelled = false;
    this.loadAbort?.abort();
    this.loadAbort = new AbortController();
    const loadSignal = this.loadAbort.signal;
    this.setStatus('loading');
    this.reportProgress(0, '準備中');
    try {
      const system = this.rom.system;
      const sysInfo = SYSTEMS[system];

      this.reportProgress(5, 'コアを選択中');
      // 設定でコアを指定していなければ、既存ステートの保存コアを優先する
      // （取り込み時の推奨名と実際のプレイコアがずれて読めなくなるのを防ぐ）
      const preferredId =
        this.preferredBackendId || (await this.backendIdFromExistingStates()) || undefined;
      const { backend, registration } = await this.awaitUnlessCancelled(
        createBackend(system, {
          preferredId,
          useWorker: this.useWorker,
        }),
        ({ backend: abandoned }) => abandoned.dispose(),
      );
      this.throwIfCancelled();
      this.backend = backend;
      this.backendId = registration.id;

      this.reportProgress(12, 'エミュレータを起動中');
      await this.awaitUnlessCancelled(
        backend.mount({
          container: this.container,
          volume: this.volume,
          pollInput: this.input.poll,
          integerScale: this.integerScale,
          shader: this.shader,
          onError: (e) => this.handleError(e),
          ...(this.events.onFps ? { onFps: this.events.onFps } : {}),
          ...(this.events.onRewindUnavailable
            ? { onRewindUnavailable: this.events.onRewindUnavailable }
            : {}),
        }),
      );
      this.throwIfCancelled();

      // PS1 は設定で選んだメモリーカードを挿す（sramKeyFor 参照）
      // レガシー ROM 単位セーブの引き継ぎはカード 0 だけ。追加カードへ流すと満杯のままになる。
      const sramKey = sramKeyFor(system, this.rom.id, this.psxMemcardId);
      if (system === 'psx' && this.psxMemcardId === DEFAULT_PSX_MEMCARD_ID) {
        await this.awaitUnlessCancelled(adoptLegacySram(sramKey, this.rom.id));
        this.throwIfCancelled();
      }

      // ここは PS1 の巨大 BIN を IndexedDB から読む段階。iPhone では数十秒かかることがある。
      // パート逐次読みで進捗を出し、メインが固まったように見えないようにする。
      this.reportProgress(28, 'ROM を読み出し中');
      const romBlobsPromise = getRomFileBlobs(
        this.rom.id,
        ({ done, total, label }) => {
          if (this.cancelled) return;
          const ratio = total > 0 ? done / total : 0;
          this.reportProgress(28 + ratio * 12, label);
        },
        loadSignal,
      );
      const sramPromise = loadSram(sramKey);
      const biosPromise = sysInfo.acceptsBios ? getBiosRecord(system) : Promise.resolve(undefined);

      // セーブ / BIOS は軽いので先に待ち、ROM 読み出し中もキャンセル可能にする
      const [storedSram, biosRec] = await this.awaitUnlessCancelled(
        Promise.all([sramPromise, biosPromise]),
      );
      this.throwIfCancelled();
      this.reportProgress(32, 'ROM データを展開中');
      let romBlobs: Record<string, Blob> | null = await this.awaitUnlessCancelled(romBlobsPromise);
      this.throwIfCancelled();

      // 追加したばかりの空カードは未フォーマット相当になり、ゲーム内セーブが拒否される。
      // pcsx と同じ空カード画像を渡してから起動する。
      // また「フラグは空きだが filesize 残骸」だと HUD は 15/15 でもゲームが容量不足にする。
      let sram = storedSram;
      if (system === 'psx') {
        const normalized = normalizePsxMemcardForBoot(sram);
        sram = normalized.data;
        if (normalized.changed) {
          await this.awaitUnlessCancelled(saveSram(sramKey, sram));
          console.info('[session] PS1 memcard normalized for boot', {
            reason: normalized.reason,
          });
        }
      }
      this.throwIfCancelled();

      if (sysInfo.requiresBios && !biosRec) {
        throw new Error(
          `${sysInfo.name} の起動には BIOS が必要です。設定画面から BIOS ファイルを追加してください。`,
        );
      }
      // pcsx は 512 KiB 以外を弾いて HLE に落ちる。空カードでも容量不足になる主因。
      if (system === 'psx' && biosRec && biosRec.data.byteLength !== 512 * 1024) {
        throw new Error(
          `PS1 BIOS のサイズが不正です（${biosRec.data.byteLength} バイト）。` +
            '512 KiB（524288 バイト）の scph5500/5501/5502.bin などを設定画面から入れ直してください。',
        );
      }

      const primaryName = this.rom.filename;
      const rom = romBlobs[primaryName] ?? Object.values(romBlobs)[0];
      if (!rom) throw new Error('ROM データが見つかりません');

      const extraFiles: Record<string, Blob> = {};
      for (const [name, data] of Object.entries(romBlobs)) {
        if (name !== primaryName) extraFiles[name] = data;
      }
      // 以降は rom / extraFiles だけを使う。全集約マップは早めに手放す。
      romBlobs = null;

      // CUE が参照する BIN が揃っているかを、コアへ渡す前に確かめる。
      // 揃っていないまま渡すと pcsx は素気なく false を返すだけで、
      // 画面には「retro_load_game failed」としか出ず原因が分からない。
      this.reportProgress(40, 'ディスク構成を確認中');
      await this.awaitUnlessCancelled(
        assertDiscSetComplete(primaryName, rom, {
          [primaryName]: rom,
          ...extraFiles,
        }),
      );
      this.throwIfCancelled();

      this.reportProgress(45, 'ゲームデータを展開中');
      await this.awaitUnlessCancelled(
        backend.load({
          system,
          romName: primaryName,
          rom,
          ...(Object.keys(extraFiles).length > 0 ? { extraFiles } : {}),
          ...(biosRec
            ? { bios: biosRec.data, biosFilename: biosRec.filename }
            : {}),
          sram,
          onProgress: (p) => {
            if (this.cancelled) return;
            // コア側 0–100 → セッション全体の 45–92
            const mapped = 45 + (p.percent / 100) * 47;
            this.reportProgress(mapped, p.label);
          },
        }),
      );
      this.throwIfCancelled();

      this.reportProgress(94, '仕上げ中');
      if (this.rewindRequested) backend.setRewindEnabled(true);
      await this.awaitUnlessCancelled(this.applyCheats());
      this.throwIfCancelled();

      backend.start();
      this.reportProgress(100, '完了');
      this.setStatus('running');

      void markPlayed(this.rom.id);
      this.startTimers();
      this.attachLifecycle();
    } catch (e) {
      // dispose / Abort で切ったときはエラー画面に出さない
      const endedByDispose =
        e instanceof Error && e.message === 'セッションが終了しました';
      const endedByAbort =
        (e instanceof DOMException && e.name === 'AbortError') ||
        (e instanceof Error && e.name === 'AbortError');
      if (
        e instanceof SessionCancelledError ||
        this.cancelled ||
        endedByDispose ||
        endedByAbort
      ) {
        this.backend?.dispose();
        this.backend = null;
        this.setStatus('idle');
        return;
      }
      this.handleError(e instanceof Error ? e : new Error(String(e)));
      throw e;
    }
  }

  private handleError(e: Error): void {
    console.error('[session]', e);
    this.setStatus('error');
    this.events.onError?.(e);
  }

  /* ------------------------------------------------------------------ */
  /* 定期処理                                                            */
  /* ------------------------------------------------------------------ */

  private startTimers(): void {
    // PS1 は 10 秒ごとの flush が複数ブロック書き込みと衝突しやすい。
    // ポーズ／pagehide／終了時だけ永続化する。
    if (this.rom.system !== 'psx') {
      this.sramTimer = window.setInterval(() => void this.flushSram(), SRAM_FLUSH_INTERVAL);
    }
    this.autosaveTimer = window.setInterval(() => void this.autosave(), AUTOSAVE_INTERVAL);
  }

  private stopTimers(): void {
    if (this.sramTimer) clearInterval(this.sramTimer);
    if (this.autosaveTimer) clearInterval(this.autosaveTimer);
    this.sramTimer = undefined;
    this.autosaveTimer = undefined;
  }

  /**
   * iOS ではアプリ切り替え・画面ロックで pagehide が飛ぶ。
   * ここで確実にセーブしないとプレイ内容が消える。
   * セーブ後はサーバーへも早めに載せて、端末切替時の競合を減らす。
   */
  private attachLifecycle(): void {
    const persist = () => {
      void this.flushSram().then(() => requestSync());
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        this.pause();
        persist();
      }
    };
    // pagehide は iOS でタブが破棄される直前に必ず来る（beforeunload は来ない）
    const onPageHide = () => {
      persist();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);

    this.detachLifecycle = () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
    };
  }

  async flushSram(): Promise<void> {
    const backend = this.backend;
    if (!backend) return;
    try {
      const sram = await backend.getSram();
      if (!sram || sram.length === 0) return;

      // PS1: 書き込み途中の不整合をライブへ「修復して戻す」と進行中セーブが消える。
      // 不整合なら永続化だけスキップ（次回起動の normalize で直る）。
      // 整合しているときだけ、そのまま IndexedDB へ書く（ライブは触らない）。
      if (this.rom.system === 'psx' && isFormattedPsxMemcard(sram)) {
        if (!isPsxMemcardConsistent(sram)) {
          console.warn('[session] PS1 memcard flush skipped (in-progress or inconsistent directory)');
          return;
        }
      }

      await saveSram(sramKeyFor(this.rom.system, this.rom.id, this.psxMemcardId), sram);
    } catch (e) {
      console.warn('[session] SRAM flush failed', e);
    }
  }

  /**
   * 実行中コアの SAVE_RAM を覗く。
   * null = コアがカードを公開していない（未接続）。
   * それ以外はディレクトリから空きブロックを数えた結果。
   */
  async peekPsxMemcard(): Promise<PsxMemcardUsage | null> {
    if (this.rom.system !== 'psx' || !this.backend) return null;
    try {
      const sram = await this.backend.getSram();
      if (!sram || sram.length === 0) return null;
      return inspectPsxMemcard(sram);
    } catch {
      return null;
    }
  }

  private async autosave(): Promise<void> {
    if (this.status !== 'running') return;
    try {
      await this.createState(AUTOSAVE_SLOT);
    } catch {
      // ステート非対応のコアもあるので黙って諦める
    }
  }

  /* ------------------------------------------------------------------ */
  /* 操作                                                                */
  /* ------------------------------------------------------------------ */

  pause(): void {
    if (this.status !== 'running') return;
    this.backend?.pause();
    this.setStatus('paused');
    // PS1 は定期 flush しないので、ポーズ時に落とす
    if (this.rom.system === 'psx') void this.flushSram();
  }

  resume(): void {
    if (this.status !== 'paused') return;
    this.backend?.resume();
    this.setStatus('running');
  }

  toggle(): void {
    if (this.status === 'running') this.pause();
    else if (this.status === 'paused') this.resume();
  }

  reset(): void {
    this.backend?.reset();
  }

  setVolume(v: number): void {
    this.volume = v;
    this.backend?.setVolume(v);
  }

  setSpeed(mult: number): void {
    this.backend?.setSpeed(mult);
  }

  /**
   * 登録済みで有効なチートをコアへ適用する。
   * 追加・削除・切り替えのたびに呼ぶ（コア側は毎回 reset して積み直す）。
   */
  async applyCheats(): Promise<boolean> {
    const backend = this.backend;
    if (!backend) return false;
    const codes = await activeCheatCodes(this.rom.id);
    // 副作用のあるメソッドなので、対応可否の判定はここでの戻り値だけを使う。
    // ゲッターから呼ぶと表示のたびにチートが消える。
    this.cheatsAvailable = backend.setCheats(codes);
    return this.cheatsAvailable;
  }

  /** コアがチートに対応しているか（起動時の適用結果を覚えておく） */
  get cheatsSupported(): boolean {
    return this.cheatsAvailable;
  }

  /**
   * マルチディスク情報。RomMeta.discs のラベルがあれば優先して載せる。
   */
  async getDiscInfo(): Promise<import('./types').DiscInfo | null> {
    const backend = this.backend;
    if (!backend) return null;
    const info = await backend.getDiscInfo();
    if (!info || info.count <= 1) {
      const metaDiscs = this.rom.discs;
      if (metaDiscs && metaDiscs.length > 1) {
        return {
          count: metaDiscs.length,
          index: info?.index ?? 0,
          labels: metaDiscs.map((d) => d.label),
        };
      }
      return info;
    }
    const metaDiscs = this.rom.discs;
    if (metaDiscs && metaDiscs.length === info.count) {
      return {
        ...info,
        labels: metaDiscs.map((d, i) => d.label || info.labels[i] || `Disc ${i + 1}`),
      };
    }
    return info;
  }

  async swapDisc(index: number): Promise<void> {
    const backend = this.backend;
    if (!backend) throw new Error('セッションが開始されていません');
    const ok = await backend.swapDisc(index);
    if (!ok) throw new Error('ディスクの切り替えに失敗しました');
  }

  /** 表示フィルタを切り替える。WebGL 非対応なら false。 */
  setShader(id: ShaderId): boolean {
    this.shader = id;
    return this.backend?.setShader(id) ?? false;
  }

  setRewindEnabled(on: boolean): void {
    this.backend?.setRewindEnabled(on);
  }

  /** 巻き戻しボタンを押している間 true にする */
  setRewinding(on: boolean): void {
    this.backend?.setRewinding(on);
  }

  get rewindStatus() {
    return this.backend?.rewindStatus ?? { available: false, active: false, seconds: 0 };
  }

  async createState(slot: number): Promise<void> {
    const backend = this.backend;
    if (!backend) throw new Error('セッションが開始されていません');

    const data = await backend.saveState();
    if (data.byteLength < 16) {
      throw new Error('セーブステートの保存に失敗しました（データが空です）');
    }
    const thumbnail = await this.captureThumbnail();

    await saveStateRecord({
      romId: this.rom.id,
      slot,
      data,
      backendId: this.backendId,
      ...(thumbnail ? { thumbnail } : {}),
    });

    // ライブラリのサムネイルも更新しておく
    if (thumbnail && slot !== AUTOSAVE_SLOT) {
      void updateRom(this.rom.id, { thumbnail }).catch(() => undefined);
    }
    // 終了を待たずサーバーへ載せる（別端末ですぐ読めるように）
    if (slot !== AUTOSAVE_SLOT) requestSync();
  }

  async restoreState(slot: number): Promise<void> {
    const backend = this.backend;
    if (!backend) throw new Error('セッションが開始されていません');

    const record = await loadStateRecord(this.rom.id, slot);
    if (!record) throw new Error('そのスロットにセーブがありません');

    if (record.backendId !== this.backendId && !compatibleStateBackend(record.backendId, this.backendId)) {
      throw new Error(
        `このステートは別のコア (${record.backendId}) で保存されています。同じコアに切り替えてから読み込んでください。`,
      );
    }

    // IndexedDB 由来のバッファは環境によって共有扱いになることがあるので必ずコピーする
    const bytes = new Uint8Array(record.data.byteLength);
    bytes.set(new Uint8Array(record.data));
    if (bytes.byteLength < 16) {
      throw new Error('セーブステートのデータが空です。My からサーバー同期してから再度お試しください。');
    }

    try {
      await backend.loadState(bytes);
    } catch (e) {
      const detail = e instanceof Error ? e.message : String(e);
      if (
        record.backendId &&
        record.backendId !== this.backendId &&
        record.backendId !== 'unknown' &&
        record.backendId !== 'test-pattern'
      ) {
        throw new Error(
          `ステートを読めませんでした（保存コア: ${record.backendId} / いま: ${this.backendId}）。設定でコアを合わせて再試行してください。詳細: ${detail}`,
        );
      }
      throw e instanceof Error ? e : new Error(detail);
    }
  }

  private async captureThumbnail(): Promise<string | undefined> {
    try {
      const blob = await this.backend?.screenshot();
      if (!blob) return undefined;
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      });
    } catch {
      return undefined;
    }
  }

  async screenshot(): Promise<Blob | null> {
    return this.backend?.screenshot() ?? null;
  }

  /** 手動ステートに残っている具体的なコア ID（新しいもの優先） */
  private async backendIdFromExistingStates(): Promise<string | undefined> {
    try {
      const states = await listStates(this.rom.id);
      const ranked = states
        .filter((s) => s.slot !== AUTOSAVE_SLOT)
        .filter(
          (s) =>
            s.backendId &&
            s.backendId !== 'unknown' &&
            s.backendId !== 'test-pattern' &&
            !s.backendId.startsWith('libretro:unknown'),
        )
        .sort((a, b) => b.createdAt - a.createdAt);
      return ranked[0]?.backendId;
    } catch {
      return undefined;
    }
  }

  /** 終了時は必ずセーブしてから破棄する。読み込み中キャンセルならセーブはしない。 */
  async dispose(): Promise<void> {
    this.cancelled = true;
    this.loadAbort?.abort();
    this.loadAbort = null;
    // 共有表に残っている同じ ROM の組み立ても切る（キャンセル後の再起動用）
    abortRomFileBlobs(this.rom.id);
    this.wakeCancelWaiters();
    if (this.disposed) return;
    this.disposed = true;
    this.stopTimers();
    this.detachLifecycle?.();
    this.detachLifecycle = null;

    if (this.status === 'running' || this.status === 'paused') {
      await this.flushSram();
      requestSync();
    }

    this.backend?.dispose();
    this.backend = null;
    this.input.reset();
    this.setStatus('idle');
  }
}

export type { SystemId };


/**
 * CUE が参照するファイルが手元に揃っているかを確認する。
 *
 * 揃っていない典型例は「取り込みの途中で BIN の保存に失敗し、
 * CUE だけが残った」状態。ライブラリ上は正常に見えるので、
 * ここで気付けないと原因不明の起動失敗になる。
 */
export async function assertDiscSetComplete(
  primaryName: string,
  primary: Blob,
  files: Record<string, Blob>,
): Promise<void> {
  const have = new Map<string, string>();
  for (const name of Object.keys(files)) have.set(normalizeKey(name), name);

  if (isM3uFilename(primaryName)) {
    const text = decodeCueBuffer(await primary.arrayBuffer())[0];
    if (!text) return;
    const entries = parseM3uEntries(text);
    if (entries.length === 0) {
      throw new Error('プレイリスト (.m3u) にディスクがありません');
    }
    for (const entry of entries) {
      const key = normalizeKey(entry);
      const actual = have.get(key);
      if (!actual) {
        throw new Error(
          `プレイリストが参照するファイルが見つかりません: ${entry}\n\n` +
            'ライブラリからこのソフトを削除し、.m3u と各ディスク（.cue/.bin）を' +
            'まとめて選んで追加し直してください。',
        );
      }
      if (isCueFilename(actual)) {
        await assertDiscSetComplete(actual, files[actual]!, files);
      }
    }
    return;
  }

  if (!isCueFilename(primaryName)) return;

  const text = decodeCueBuffer(await primary.arrayBuffer())[0];
  if (!text) return;

  const missing = parseCueFileRefs(text).filter((ref) => !have.has(normalizeKey(ref)));
  if (missing.length === 0) return;

  throw new Error(
    `CUE が参照するファイルが見つかりません: ${missing.join(', ')}\n\n` +
      'ライブラリからこのソフトを削除し、.cue と .bin を' +
      'まとめて選んで追加し直してください。' +
      '（片方だけを追加すると、この状態になります）',
  );
}

/**
 * 取り込み時に test-pattern / unknown へ落ちたステートも読めるようにする。
 * サーバー経由だとメタ（x-ms-meta-backendid）が欠けることがあり、そのままだと
 * 「別のコア」扱いで読込不能になる。
 *
 * 取り込み時は機種の推奨コア名を押す一方、設定で別コアを選んでいると不一致になる。
 * 同じ libretro 系なら一旦試し、失敗は unserialize 側のエラーに任せる。
 */
function compatibleStateBackend(savedId: string, currentId: string): boolean {
  if (savedId === currentId) return true;
  if (
    !savedId ||
    savedId === 'unknown' ||
    savedId === 'test-pattern' ||
    savedId.startsWith('libretro:unknown')
  ) {
    return true;
  }
  if (savedId.startsWith('libretro:') && currentId.startsWith('libretro:')) {
    return true;
  }
  return false;
}

/**
 * 共有カードがまだ無いときに限り、そのソフト単体のセーブを引き継ぐ。
 *
 * PS1 のセーブは以前 ROM ごとに分かれていた。共有へ切り替えたときに
 * 既存のセーブが見えなくなると「消えた」ように映るので、
 * **最初の 1 本ぶんだけ**共有カードとして採用する。
 *
 * 採用後は元レコードを消す。残すとサーバー同期で「使っていない旧カード」が
 * 競合を起こし続けるため。
 */
async function adoptLegacySram(sharedKey: string, romId: string): Promise<void> {
  try {
    if (await getSaveRecord(sharedKey)) return;
    const legacy = await getSaveRecord(romId);
    if (!legacy || legacy.data.byteLength === 0) return;
    await saveSram(sharedKey, new Uint8Array(legacy.data));
    await deleteSram(romId);
  } catch (e) {
    // 引き継ぎに失敗しても起動は妨げない（空のカードで始まるだけ）
    console.warn('[session] メモリーカードの引き継ぎに失敗', e);
  }
}
